import React, { useState, useEffect, useMemo, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { ChevronLeft, Volume2, Swords } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { flashcardActions } from '../../redux/actions/flashcard.actions';
import { flashcardService } from '../../services/flashcard.service';
import { leaderboardService } from '../../services/leaderboard.service';
import { apiClient } from '../../helpers';
import { ChallengeModal } from './ChallengeModal';
import ChallengeResultModal from './ChallengeResultModal';
import ChallengeSentModal from './ChallengeSentModal';
import LeaderboardModal from './LeaderboardModal';

// ============= ANIMATIONS =============
const slideIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const correctMatch = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.1); }
  100% { transform: scale(0); opacity: 0; }
`;

const wrongShake = keyframes`
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-10px); }
  75% { transform: translateX(10px); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const celebrateConfetti = keyframes`
  0% { transform: translate(0, 0) rotate(0deg); opacity: 1; }
  100% { transform: translate(var(--x), var(--y)) rotate(360deg); opacity: 0; }
`;

// ============= STYLED COMPONENTS =============
const Container = styled.div`
  min-height: 100vh;
  background: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 20px 0;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  position: relative;
`;

const TopBar = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  padding: 0 20px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: #f3f4f6;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  color: #1f2937;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;

  &:hover {
    background: #e5e7eb;
    border-color: #9ca3af;
    color: #000;
  }
`;

const TimerDisplay = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  font-size: 18px;
  font-weight: bold;
  color: #667eea;
  font-family: 'Courier New', monospace;
  z-index: 100;
  background: white;
  padding: 6px 12px;
  border-radius: 8px;
  border: 2px solid #667eea;

  @media (max-width: 768px) {
    font-size: 16px;
    padding: 5px 10px;
  }
`;

const GameBoard = styled.div`
  width: 100%;
  height: 95vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 20px;
  animation: ${slideIn} 0.5s ease-out;
  position: relative;
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 12px;
  width: 100%;
  height: 100%;
  padding: 0;
  align-items: stretch;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(6, 1fr);
    gap: 12px;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 10px;
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
  }
`;

interface CardProps {
  $matched?: boolean;
  $flipped?: boolean;
  $shaking?: boolean;
  $selected?: boolean;
  $wrong?: boolean;
  $isBack?: boolean;
}

const Card = styled.div<CardProps>`
  background: ${props => props.$isBack ? '#fffacd' : 'white'};
  border: 3px solid #e0e0e0;
  border-radius: 12px;
  cursor: pointer;
  font-size: 25px;
  font-weight: 700;
  color: #333;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 2px;
  transition: all 0.3s ease;
  position: relative;
  user-select: none;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
  line-height: 1.2;
  word-wrap: break-word;
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: normal;
  overflow: hidden;
  min-height: 0;

  @media (max-width: 768px) {
    font-size: 14px;
    border-width: 2px;
    border-radius: 8px;
    padding: 4px;
  }

  @media (max-width: 480px) {
    font-size: 11px;
    border-width: 2px;
    border-radius: 6px;
    padding: 2px;
  }
  
  ${props => props.$matched && css`
    animation: ${correctMatch} 0.6s ease-out forwards;
    cursor: default;
    pointer-events: none;
    opacity: 0;
  `}

  ${props => props.$selected && css`
    border-color: #667eea;
    background: ${props.$isBack ? '#fff9e6' : '#f0f4ff'};
    box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.2);
  `}

  ${props => props.$wrong && css`
    animation: ${wrongShake} 0.4s ease-in-out;
    border-color: #dc3545;
    box-shadow: 0 0 0 4px rgba(220, 53, 69, 0.2);
  `}

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
    border-color: #667eea;
  }
`;

const AudioButton = styled.button`
  position: absolute;
  top: 5px;
  right: 5px;
  background: rgba(255, 255, 255, 0.8);
  border: none;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #667eea;
  transition: all 0.2s ease;

  &:hover {
    background: white;
    transform: scale(1.1);
  }
`;

const CompleteScreen = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: ${slideIn} 0.4s ease-out;
`;

const CompletionCard = styled.div`
  background: white;
  border-radius: 30px;
  padding: 60px;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  animation: ${slideIn} 0.5s ease-out;

  @media (max-width: 768px) {
    padding: 40px 30px;
    border-radius: 20px;
  }

  @media (max-width: 480px) {
    padding: 30px 20px;
    border-radius: 16px;
  }
`;

const CelebrationEmoji = styled.div`
  font-size: 80px;
  margin-bottom: 20px;
  animation: ${pulse} 0.6s ease-in-out infinite;

  @media (max-width: 768px) {
    font-size: 60px;
    margin-bottom: 16px;
  }

  @media (max-width: 480px) {
    font-size: 48px;
    margin-bottom: 12px;
  }
`;

const CompletionTitle = styled.h2`
  font-size: 48px;
  color: #667eea;
  margin: 0 0 20px 0;
  text-shadow: 0 2px 10px rgba(102, 126, 234, 0.2);

  @media (max-width: 768px) {
    font-size: 32px;
    margin: 0 0 16px 0;
  }

  @media (max-width: 480px) {
    font-size: 24px;
    margin: 0 0 12px 0;
  }
`;

const CompletionMessage = styled.p`
  font-size: 20px;
  color: #666;
  margin-bottom: 30px;

  @media (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 20px;
  }

  @media (max-width: 480px) {
    font-size: 14px;
    margin-bottom: 16px;
  }
`;

const TimeDisplay = styled.div`
  font-size: 32px;
  font-weight: bold;
  color: #28a745;
  font-family: 'Courier New', monospace;
  margin-bottom: 30px;
  background: #f0f8f4;
  padding: 20px;
  border-radius: 10px;

  @media (max-width: 768px) {
    font-size: 24px;
    padding: 16px;
    margin-bottom: 20px;
  }

  @media (max-width: 480px) {
    font-size: 18px;
    padding: 12px;
    margin-bottom: 16px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 15px;
  justify-content: center;
  margin-top: 30px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 12px;
    margin-top: 20px;
  }

  @media (max-width: 480px) {
    gap: 10px;
    margin-top: 16px;
  }
`;

const RetryButton = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 15px 40px;
  font-size: 18px;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 25px rgba(102, 126, 234, 0.3);
  }

  @media (max-width: 768px) {
    padding: 12px 32px;
    font-size: 16px;
  }

  @media (max-width: 480px) {
    padding: 10px 24px;
    font-size: 14px;
  }
`;

const BackDetailButton = styled(RetryButton)`
  background: white;
  color: #667eea;
  border: 2px solid #667eea;

  &:hover {
    background: #f8f9ff;
  }
`;

const Confetti = styled.div`
  position: fixed;
  pointer-events: none;
`;

const ConfettiPiece = styled.div<{ moveX?: number; moveY?: number; color?: string; size?: number }>`
  position: absolute;
  animation: ${celebrateConfetti} 1s ease-out forwards;
  --x: ${props => `${props.moveX || 0}px`};
  --y: ${props => `${props.moveY || 0}px`};
`;

import { css } from 'styled-components';

// ============= TYPES =============
interface Card {
  id: string;
  text: string;
  pairId: string;
  audioUrl?: string;
  type: 'front' | 'back';
}

interface MatchingGamePageProps {
  setId: number;
  onBack: () => void;
  challengeState?: {
    challengeId: number;
    isChallenge: boolean;
    gameType: 'quiz' | 'matching';
    isAccepter?: boolean; // true if this player is accepting a challenge
  } | null;
}

// ============= HELPER FUNCTIONS =============
const createConfetti = (x: number, y: number) => {
  const confetti = [];
  const colors = ['#667eea', '#764ba2', '#28a745', '#ffc107', '#dc3545'];

  for (let i = 0; i < 30; i++) {
    const angle = (Math.random() * Math.PI * 2);
    const velocity = 5 + Math.random() * 10;
    const moveX = Math.cos(angle) * velocity * 50;
    const moveY = Math.sin(angle) * velocity * 50 - 100;

    confetti.push({
      x: moveX,
      y: moveY,
      color: colors[Math.floor(Math.random() * colors.length)],
      size: 8 + Math.random() * 12,
    });
  }

  return confetti;
};

// ============= MAIN COMPONENT =============
const MatchingGamePage: React.FC<MatchingGamePageProps> = ({ setId, onBack, challengeState }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { cards = [], sets = [] } = useSelector((state: any) => state.flashcard);
  const authState = useSelector((state: any) => state.authentication);
  const user = authState?.userInfo;

  // Local state for set & cards — loaded directly via API so guests/cross-class sets work
  const [localSet, setLocalSet] = useState<any>(null);
  const [localCards, setLocalCards] = useState<any[]>([]);

  const currentSet = localSet;
  const setCards = useMemo(() => localCards, [localCards]);

  const [gameCards, setGameCards] = useState<Card[]>([]);
  const [selectedCards, setSelectedCards] = useState<string[]>([]);
  const [matchedPairs, setMatchedPairs] = useState<Set<string>>(new Set());
  const [wrongCardId, setWrongCardId] = useState<string | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [confetti, setConfetti] = useState<any[]>([]);
  const backgroundAudioRef = React.useRef<AudioContext | null>(null);
  const backgroundMusicPlayingRef = React.useRef<boolean>(false);
  const backgroundOscillatorsRef = React.useRef<OscillatorNode[]>([]);

  // Admin (role=0) or Teacher (role=1) keep ranking in SetDetail; others see it in result screen
  const isAdminOrTeacher = user?.role === 0 || user?.role === 1;

  // Leaderboard modal state (for non-admin/teacher users in result screen)
  const [showLeaderboard, setShowLeaderboard] = useState(false);

  // Challenge modal state
  const [showChallengeModal, setShowChallengeModal] = useState(false);
  const [currentStudentId, setCurrentStudentId] = useState<number | null>(null);
  const [currentStudentGifts, setCurrentStudentGifts] = useState(0);

  // Challenge result modal state
  const [showResultModal, setShowResultModal] = useState(false);
  const [challengeResult, setChallengeResult] = useState<{
    challengerName: string;
    challengerAvatar?: string;
    challengerScore: number;
    challengerTime?: number;
    accepterName: string;
    accepterAvatar?: string;
    accepterScore: number;
    accepterTime?: number;
    winner: 'challenger' | 'accepter' | 'tie';
    giftAmount: number;
    isCurrentUserWinner: boolean;
    gameType: 'quiz' | 'matching';
  } | null>(null);

  // Challenge sent modal state
  const [showSentModal, setShowSentModal] = useState(false);
  const [sentAccepterName, setSentAccepterName] = useState('');

  // Load student info for challenge
  useEffect(() => {
    const loadStudentInfo = async () => {
      try {
        let studentId = user?.studentId;

        // Try to get from JWT if not available
        if (!studentId) {
          const accessToken = localStorage.getItem('accessToken');
          if (accessToken) {
            try {
              const parts = accessToken.split('.');
              if (parts.length === 3) {
                const payload = JSON.parse(atob(parts[1]));
                if (payload.studentId) {
                  studentId = payload.studentId;
                }
              }
            } catch (e) {
              console.log('Failed to decode JWT');
            }
          }
        }

        if (studentId) {
          setCurrentStudentId(studentId);
          try {
            const res = await apiClient.get(`/api/students/${studentId}`);
            if (res.data) {
              setCurrentStudentGifts(res.data.gift || 0);
            }
          } catch (err) {
            console.log('Could not fetch student gifts');
          }
        }
      } catch (error) {
        console.error('Error loading student info:', error);
      }
    };

    loadStudentInfo();
  }, [user?.studentId]);

  // Load set and cards directly via API (supports guests and cross-class sets)
  useEffect(() => {
    const loadData = async () => {
      try {
        const [setData, cardsData] = await Promise.all([
          flashcardService.getSetById(setId),
          flashcardService.getAllCards(setId),
        ]);
        setLocalSet(setData);
        setLocalCards(Array.isArray(cardsData) ? cardsData : []);
      } catch (err) {
        console.error('[MatchingGamePage] Failed to load set/cards:', err);
      }
    };
    loadData();
  }, [setId]);

  // Initialize game
  useEffect(() => {
    if (setCards.length === 0) return;

    // Take max 14 cards (or random if more than 14)
    let selectedCards = setCards;
    if (setCards.length > 14) {
      selectedCards = [];
      const indices = new Set<number>();
      while (indices.size < 14) {
        indices.add(Math.floor(Math.random() * setCards.length));
      }
      selectedCards = Array.from(indices).map(i => setCards[i]);
    }

    // Create card pairs (front & back)
    const cards: Card[] = selectedCards.flatMap((card: any, idx: number) => [
      {
        id: `front-${idx}`,
        text: card.front,
        pairId: `back-${idx}`,
        audioUrl: card.audio_url,
        type: 'front',
      },
      {
        id: `back-${idx}`,
        text: card.back,
        pairId: `front-${idx}`,
        audioUrl: card.audio_url,
        type: 'back',
      },
    ]);

    // Shuffle cards
    const shuffled = cards.sort(() => Math.random() - 0.5);
    setGameCards(shuffled);
    setSelectedCards([]);
    setMatchedPairs(new Set());
    setIsComplete(false);
    setElapsedTime(0);

    // Play background music
    playBackgroundMusic();
  }, [setCards]);

  // Timer effect
  useEffect(() => {
    if (isComplete) return;

    const timer = setInterval(() => {
      setElapsedTime(prev => prev + 10);
    }, 10);

    return () => clearInterval(timer);
  }, [isComplete]);

  // Stop background music when game complete
  useEffect(() => {
    if (isComplete) {
      stopBackgroundMusic();

      // Play celebration sound
      playCelebrationSound();

      // Generate confetti
      generateConfetti();

      // Save to leaderboard if user is a student
      if (user?.studentId) {
        leaderboardService.saveGameCompletion(
          Number(user.studentId),
          setId,
          'matching',
          elapsedTime
        ).catch(err => console.error('Failed to save leaderboard:', err));
      }

      // Auto-submit score if this is a challenge and user is the accepter
      if (challengeState?.isChallenge && challengeState?.isAccepter && challengeState?.challengeId) {
        console.log('🎯 Auto-submitting accepter score for challenge...');
        const score = matchedPairs.size / 2; // Each pair counts as 1 correct match
        apiClient.patch(`/api/challenges/${challengeState.challengeId}/submit-accepter-score`, {
          score,
          time: elapsedTime, // Include time for tiebreaker
        }).then((response) => {
          console.log('✅ Challenge score submitted successfully');
          // Show result modal with challenge details
          const challenge = response.data;
          const isAccepterWinner = challenge.winner === 'accepter';
          setChallengeResult({
            challengerName: challenge.challenger?.name || 'Người thách đấu',
            challengerAvatar: challenge.challenger?.avatar,
            challengerScore: challenge.challengerScore,
            challengerTime: challenge.challengerTime,
            accepterName: challenge.accepter?.name || 'Bạn',
            accepterAvatar: challenge.accepter?.avatar,
            accepterScore: challenge.accepterScore,
            accepterTime: challenge.accepterTime,
            winner: challenge.winner,
            giftAmount: challenge.giftAmount,
            isCurrentUserWinner: isAccepterWinner,
            gameType: 'matching',
          });
          setShowResultModal(true);
        }).catch(err => {
          console.error('❌ Failed to submit challenge score:', err);
        });
      }
    }
  }, [isComplete]);

  // Cleanup background music on unmount
  useEffect(() => {
    return () => {
      stopBackgroundMusic();
    };
  }, []);

  // Check for matches
  useEffect(() => {
    if (selectedCards.length !== 2) return;

    const [card1Id, card2Id] = selectedCards;
    const card1 = gameCards.find(c => c.id === card1Id);
    const card2 = gameCards.find(c => c.id === card2Id);

    if (card1?.pairId === card2Id) {
      // Match found
      playMatchSound();
      setMatchedPairs(prev => new Set([...prev, card1Id, card2Id]));
      setTimeout(() => {
        setSelectedCards([]);
        setWrongCardId(null);
      }, 300);
    } else {
      // No match
      playWrongSound();
      setWrongCardId(card1Id);
      setTimeout(() => {
        setWrongCardId(null);
        setSelectedCards([]);
      }, 600);
    }
  }, [selectedCards]);

  // Check if game is complete
  useEffect(() => {
    if (gameCards.length > 0 && matchedPairs.size === gameCards.length && gameCards.length > 0) {
      setIsComplete(true);
      playCelebrationSound();
      // Create confetti
      const newConfetti = createConfetti(window.innerWidth / 2, window.innerHeight / 2);
      setConfetti(newConfetti);
    }
  }, [matchedPairs, gameCards.length]);

  // Sound effects
  const playMatchSound = () => {
    if (!soundEnabled) return;
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const notes = [523, 659, 784]; // C, E, G
    let time = audioContext.currentTime;

    notes.forEach((freq) => {
      const osc = audioContext.createOscillator();
      const gain = audioContext.createGain();
      osc.connect(gain);
      gain.connect(audioContext.destination);
      osc.frequency.value = freq;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.3, time);
      gain.gain.exponentialRampToValueAtTime(0.01, time + 0.15);
      osc.start(time);
      osc.stop(time + 0.15);
      time += 0.1;
    });
  };

  const playWrongSound = () => {
    if (!soundEnabled) return;
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const notes = [523, 440]; // C, A (descending)
    let time = audioContext.currentTime;

    notes.forEach((freq) => {
      const osc = audioContext.createOscillator();
      const gain = audioContext.createGain();
      osc.connect(gain);
      gain.connect(audioContext.destination);
      osc.frequency.value = freq;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.3, time);
      gain.gain.exponentialRampToValueAtTime(0.01, time + 0.2);
      osc.start(time);
      osc.stop(time + 0.2);
      time += 0.15;
    });
  };

  const playCelebrationSound = () => {
    if (!soundEnabled) return;
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const notes = [523, 659, 784, 1047]; // C, E, G, C (up)
    let time = audioContext.currentTime;

    notes.forEach((freq) => {
      const osc = audioContext.createOscillator();
      const gain = audioContext.createGain();
      osc.connect(gain);
      gain.connect(audioContext.destination);
      osc.frequency.value = freq;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.3, time);
      gain.gain.exponentialRampToValueAtTime(0.01, time + 0.2);
      osc.start(time);
      osc.stop(time + 0.2);
      time += 0.1;
    });
  };

  const playBackgroundMusic = () => {
    if (!soundEnabled) return;

    // Prevent multiple instances
    if (backgroundMusicPlayingRef.current) {
      return;
    }

    try {
      // Stop any existing audio context first
      stopBackgroundMusic();

      // Create fresh audio context
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      backgroundAudioRef.current = audioContext;
      backgroundMusicPlayingRef.current = true;
      backgroundOscillatorsRef.current = [];

      // Simple pleasant loop: C-D-E-F (ascending scale, repeating)
      const notes = [262, 294, 330, 349]; // C, D, E, F
      const duration = 0.3; // Each note duration

      // Create oscillators and schedule them
      const createLoopNotes = (startTime: number, endTime: number) => {
        let time = startTime;
        while (time < endTime) {
          notes.forEach((freq) => {
            const osc = audioContext.createOscillator();
            const gain = audioContext.createGain();

            osc.connect(gain);
            gain.connect(audioContext.destination);

            osc.frequency.value = freq;
            osc.type = 'sine';

            // Soft volume for background
            gain.gain.setValueAtTime(0.05, time);
            gain.gain.exponentialRampToValueAtTime(0.01, time + duration);

            osc.start(time);
            osc.stop(time + duration);

            backgroundOscillatorsRef.current.push(osc);

            time += duration;
          });
        }
      };

      // Play for 10 minutes (600 seconds)
      createLoopNotes(audioContext.currentTime, audioContext.currentTime + 600);
    } catch (err) {
      console.log('Background music not available');
      backgroundMusicPlayingRef.current = false;
    }
  };

  const stopBackgroundMusic = () => {
    try {
      // Stop all oscillators
      backgroundOscillatorsRef.current.forEach((osc) => {
        try {
          osc.stop(0);
          osc.disconnect();
        } catch (err) {
          // Already stopped
        }
      });
      backgroundOscillatorsRef.current = [];

      // Close audio context
      const ctx = backgroundAudioRef.current;
      if (ctx && ctx.state !== 'closed') {
        ctx.close().catch(() => { });
      }
    } catch (err) {
      console.log('Error stopping background music');
    }
    backgroundAudioRef.current = null;
    backgroundMusicPlayingRef.current = false;
  };

  const generateConfetti = () => {
    const pieces = [];
    const colors = ['#FFD700', '#FF69B4', '#1E90FF', '#32CD32', '#FF6347', '#FFB6C1'];
    for (let i = 0; i < 40; i++) {
      pieces.push({
        x: Math.random() * 400 - 200,
        y: Math.random() * 400 + 100,
        color: colors[Math.floor(Math.random() * colors.length)],
        size: Math.random() * 6 + 4,
      });
    }
    setConfetti(pieces);

    // Clear confetti after animation completes
    setTimeout(() => setConfetti([]), 2000);
  };

  const handleCardClick = (cardId: string) => {
    if (selectedCards.includes(cardId) || matchedPairs.has(cardId) || selectedCards.length >= 2) {
      return;
    }
    setSelectedCards([...selectedCards, cardId]);
  };

  const handlePlayAudio = (audioUrl?: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (audioUrl) {
      const audio = new Audio(audioUrl);
      audio.play().catch(err => console.error('Error playing audio:', err));
    }
  };

  const formatTime = (millis: number) => {
    const totalSeconds = Math.floor(millis / 1000);
    const ms = Math.floor((millis % 1000) / 10);
    return `${totalSeconds.toString().padStart(2, '0')}:${ms.toString().padStart(2, '0')}`;
  };

  const handleSubmitChallengeScore = async () => {
    if (!challengeState?.challengeId || !user?.id) {
      alert('Missing challenge information');
      return;
    }

    try {
      await apiClient.patch(`/api/challenges/${challengeState.challengeId}/submit-challenger-score`, {
        score: matchedPairs.size / 2, // Each pair counts as 1 correct match
      });

      alert('🎯 Kết quả của bạn đã được gửi! Đợi đối thủ hoàn thành để so kết quả.');
      navigate('/student');
    } catch (error) {
      console.error('Failed to submit challenge score:', error);
      alert('Không thể gửi kết quả thách đấu');
    }
  };

  const handleChallengeSubmit = async (accepterId: number, accepterName: string, giftAmount: number, gameType: 'quiz' | 'matching', challengerScore?: number) => {
    if (!currentStudentId) {
      alert('Vui lòng đăng nhập để thách đấu');
      return;
    }

    try {
      await apiClient.post('/api/challenges', {
        challengerId: currentStudentId,
        accepterId,
        setId,
        gameType,
        giftAmount,
        challengerScore, // Include score when creating challenge
        challengerTime: elapsedTime, // Include time when creating challenge
      });

      setCurrentStudentGifts(Math.max(0, currentStudentGifts - giftAmount));
      setSentAccepterName(accepterName);
      setShowSentModal(true);
    } catch (error: any) {
      alert(`Lỗi: ${error?.response?.data?.message || error.message || 'Không thể gửi thách đấu'}`);
    }
  };

  const handleSentModalClose = () => {
    setShowSentModal(false);
    navigate('/student');
  };

  const handleRetry = () => {
    const shuffled = gameCards.sort(() => Math.random() - 0.5);
    setGameCards(shuffled);
    setSelectedCards([]);
    setMatchedPairs(new Set());
    setIsComplete(false);
    setElapsedTime(0);
    setConfetti([]);
  };

  if (!currentSet || gameCards.length === 0) {
    return (
      <Container>
        <TopBar>
          <BackButton onClick={onBack}>
            <ChevronLeft size={20} />
            Quay lại
          </BackButton>
        </TopBar>
        <GameBoard>
          <p style={{ textAlign: 'center' }}>Đang tải trò chơi...</p>
        </GameBoard>
      </Container>
    );
  }

  return (
    <Container>
      <TopBar>
        <BackButton onClick={onBack}>
          <ChevronLeft size={20} />
          Quay lại
        </BackButton>
      </TopBar>

      <TimerDisplay>⏱️ {formatTime(elapsedTime)}</TimerDisplay>

      <GameBoard>
        <CardsGrid>
          {gameCards.map((card) => (
            <Card
              key={card.id}
              $matched={matchedPairs.has(card.id)}
              $selected={selectedCards.includes(card.id) && !matchedPairs.has(card.id)}
              $wrong={wrongCardId === card.id}
              $isBack={card.type === 'back'}
              onClick={() => handleCardClick(card.id)}
              style={{ pointerEvents: (matchedPairs.has(card.id) || selectedCards.length >= 2) ? 'none' : 'auto' }}
            >
              {card.audioUrl && card.type === 'front' && (
                <AudioButton onClick={(e) => handlePlayAudio(card.audioUrl, e)}>
                  <Volume2 size={16} />
                </AudioButton>
              )}
              {card.text}
            </Card>
          ))}
        </CardsGrid>
      </GameBoard>

      {isComplete && (
        <CompleteScreen>
          <CompletionCard>
            <CelebrationEmoji>🎉✨🌟</CelebrationEmoji>
            <CompletionTitle>Chúc Mừng!</CompletionTitle>
            <CompletionMessage>Bạn đã hoàn thành trò chơi ghép thẻ!</CompletionMessage>
            <TimeDisplay>⏱️ {formatTime(elapsedTime)}</TimeDisplay>
            <ButtonGroup>
              <RetryButton onClick={handleRetry}>Chơi Lại</RetryButton>
              {challengeState ? (
                <BackDetailButton onClick={handleSubmitChallengeScore}>🎯 Gửi Kết Quả Thách Đấu</BackDetailButton>
              ) : (
                <>
                  {!isAdminOrTeacher && (
                    <BackDetailButton
                      onClick={() => setShowLeaderboard(true)}
                      style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: 'white', border: 'none' }}
                    >
                      🏆 Xếp Hạng
                    </BackDetailButton>
                  )}
                  {currentStudentId && (
                    <BackDetailButton onClick={() => setShowChallengeModal(true)} style={{ background: 'linear-gradient(90deg, #ff6b6b 0%, #ee5a6f 100%)' }}>
                      <Swords size={20} style={{ marginRight: '8px' }} />
                      Thách Đấu
                    </BackDetailButton>
                  )}
                  <BackDetailButton onClick={onBack}>Quay Lại Chi Tiết Tập Từ</BackDetailButton>
                </>
              )}
            </ButtonGroup>
          </CompletionCard>

          {/* Challenge Modal */}
          <ChallengeModal
            isOpen={showChallengeModal}
            onClose={() => setShowChallengeModal(false)}
            onSubmit={handleChallengeSubmit}
            gameType="matching"
            currentStudentId={currentStudentId || 0}
            currentStudentGifts={currentStudentGifts}
            challengerScore={matchedPairs.size / 2}
          />

          {/* Leaderboard Modal (for non-admin/teacher users) */}
          {!isAdminOrTeacher && (
            <LeaderboardModal
              setId={setId}
              gameType="matching"
              isOpen={showLeaderboard}
              onClose={() => setShowLeaderboard(false)}
              title="🏆 Xếp Hạng Ghép Thẻ"
            />
          )}

          {/* Challenge Result Modal */}
          <ChallengeResultModal
            isOpen={showResultModal}
            onClose={() => {
              setShowResultModal(false);
              navigate('/student');
            }}
            result={challengeResult}
          />

          {/* Challenge Sent Modal */}
          <ChallengeSentModal
            isOpen={showSentModal}
            onClose={handleSentModalClose}
            accepterName={sentAccepterName}
          />
        </CompleteScreen>
      )}

      {confetti.map((item, idx) => (
        <ConfettiPiece
          key={idx}
          moveX={item.x}
          moveY={item.y}
          color={item.color}
          size={item.size}
          style={{
            left: '50%',
            top: '50%',
            width: `${item.size}px`,
            height: `${item.size}px`,
            backgroundColor: item.color,
            borderRadius: '50%',
          }}
        />
      ))}
    </Container>
  );
};

export default MatchingGamePage;
